// src/context/ThemeContext.jsx
//
// Direction A — Black + Cyan
// Inter (sans) / Fraunces (display serif).
// Raw token values live in src/theme/tokens.js — use those directly when the
// MUI palette doesn't carry the right semantic key (e.g. brand cyan, gold).
import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { tokens, tokensDark, fonts, radii } from "../theme/tokens";

const ThemeContext = createContext();

const buildPalette = (mode) => {
  const t = mode === "dark" ? tokensDark : tokens;
  return {
    mode,
    primary: { main: t.ink, contrastText: "#ffffff" },
    secondary: { main: t.gold, contrastText: t.ink },
    info: { main: t.cyanDeep, light: t.cyan, dark: t.cyanDeep, contrastText: t.ink },
    error: { main: t.danger },
    warning: { main: t.warning },
    success: { main: t.success },
    divider: t.border,
    background: { default: t.bg, paper: t.surface },
    text: { primary: t.ink, secondary: t.inkMuted, disabled: t.inkSoft },
    // Custom slot — accessed as theme.palette.brand.gold etc.
    brand: {
      ink: t.ink,
      inkMuted: t.inkMuted,
      inkSoft: t.inkSoft,
      black: t.black,
      blackDeep: t.blackDeep,
      blackSoft: t.blackSoft,
      gold: t.gold,
      goldDeep: t.goldDeep,
      goldSoft: t.goldSoft,
      goldTint: t.goldTint,
      cyan: t.cyan,
      cyanDeep: t.cyanDeep,
      cyanSoft: t.cyanSoft,
      surface: t.surface,
      surfaceMuted: t.surfaceMuted,
      border: t.border,
      borderStrong: t.borderStrong,
      bg: t.bg,
    },
  };
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("themeMode");
    return saved === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  const theme = useMemo(() => {
    const palette = buildPalette(mode);
    return createTheme({
      palette,
      shape: { borderRadius: radii.card },
      typography: {
        fontFamily: fonts.sans,
        h1: { fontFamily: fonts.serif, fontWeight: 500, letterSpacing: "-0.02em" },
        h2: { fontFamily: fonts.serif, fontWeight: 500, letterSpacing: "-0.015em" },
        h3: { fontFamily: fonts.serif, fontWeight: 500, letterSpacing: "-0.015em" },
        h4: { fontFamily: fonts.serif, fontWeight: 500, letterSpacing: "-0.015em" },
        h5: { fontFamily: fonts.serif, fontWeight: 600, letterSpacing: "-0.01em" },
        h6: { fontFamily: fonts.serif, fontWeight: 600 },
        button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.005em" },
        body1: { fontSize: "14px" },
        body2: { fontSize: "13px" },
        // Eyebrow style — small caps, used for section labels
        overline: {
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          lineHeight: 1.2,
        },
      },
      // No drop-shadows in this design — borders only.
      shadows: ["none", ...Array(24).fill("none")],
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: palette.background.default,
              color: palette.text.primary,
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: radii.button,
              fontWeight: 600,
              fontSize: "13.5px",
              padding: "9px 16px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
              "&:active": { transform: "translateY(1px)" },
            },
            containedPrimary: {
              backgroundColor: palette.brand.black,
              color: "#fff",
              "&:hover": { backgroundColor: palette.brand.blackSoft },
            },
            outlined: {
              borderColor: palette.brand.border,
              color: palette.brand.ink,
              "&:hover": {
                backgroundColor: palette.brand.surfaceMuted,
                borderColor: palette.brand.borderStrong,
              },
            },
          },
        },
        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              backgroundImage: "none",
              border: `1px solid ${palette.brand.border}`,
              borderRadius: radii.card,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: radii.input,
              backgroundColor: palette.background.paper,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.brand.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.brand.borderStrong,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.brand.ink,
                borderWidth: 1,
              },
              "&.Mui-focused": {
                boxShadow: "0 0 0 3px rgba(0,0,0,0.06)",
              },
            },
            input: { padding: "11px 14px", fontSize: 14 },
          },
        },
        MuiTableCell: {
          styleOverrides: { root: { borderColor: palette.brand.border } },
        },
        MuiDivider: {
          styleOverrides: { root: { borderColor: palette.brand.border } },
        },
        // Filled alerts (toasts / Snackbars): corporate black + gold/cyan accents — not MUI default green.
        MuiAlert: {
          styleOverrides: {
            filledSuccess: {
              backgroundColor: palette.brand.black,
              color: "rgba(255,255,255,0.95)",
              "& .MuiAlert-icon": { color: palette.brand.gold },
            },
            filledError: {
              backgroundColor: palette.brand.blackDeep,
              color: "rgba(255,255,255,0.95)",
              "& .MuiAlert-icon": { color: palette.error.main },
            },
            filledWarning: {
              backgroundColor: palette.brand.blackSoft,
              color: "rgba(255,255,255,0.95)",
              "& .MuiAlert-icon": { color: palette.brand.gold },
            },
            filledInfo: {
              backgroundColor: palette.brand.black,
              color: "rgba(255,255,255,0.95)",
              "& .MuiAlert-icon": { color: palette.brand.cyan },
            },
            filled: {
              "& .MuiAlert-action .MuiIconButton-root": {
                color: "rgba(255,255,255,0.55)",
              },
              "& .MuiAlert-action .MuiIconButton-root:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider
      value={{
        mode,
        toggleTheme,
        tokens: mode === "dark" ? tokensDark : tokens,
      }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within ThemeContextProvider");
  }
  return context;
};
